import React from 'react';
import { Award, TrendingUp, ArrowUp } from 'lucide-react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';

interface BoundingBoxData {
  x_percent: number;
  y_percent: number;
  width_percent: number;
  height_percent: number;
}

interface DifferentiatingElementData {
  name: string;
  type: string;
  variant_index: number;
  preference_score: number;
  most_count: number;
  least_count: number;
  driver_percent: number;
  blocker_percent: number;
  positive_quotes?: string[];
  negative_quotes?: string[];
  key_insight?: string;
  bounding_box?: BoundingBoxData | null;
}

interface VariantRanking {
  variant_index: number;
  variant_label: string;
  overall_score: number;
  rank: number;
}

interface WinnerData {
  variant_index: number;
  variant_label: string;
  overall_score: number;
  why_it_wins: string;
}

interface KeyDifference {
  element_name: string;
  insight: string;
  favors_variant: number;
}

interface PackagingVariantComparisonData {
  variants: any[];
  ranking: VariantRanking[];
  winner: WinnerData;
  differentiating_elements?: DifferentiatingElementData[];
  key_differences?: KeyDifference[];
  maxdiff_total_sets?: number;
  // Legacy fields for backwards compat
  maxdiff_rankings?: any[];
  claim_bounding_boxes?: any[];
  design_diffs?: any[];
}

interface ContentData {
  name?: string;
  data?: {
    brand?: string;
    goal?: string;
    variant_images?: string[];
  };
  [key: string]: any;
}

interface PackagingVariantComparisonResultsProps {
  data: PackagingVariantComparisonData;
  contentData?: ContentData;
  isPreparingExport?: boolean;
  isPrintMode?: boolean;
}

const PackagingVariantComparisonResults: React.FC<PackagingVariantComparisonResultsProps> = ({
  data,
  contentData,
  isPrintMode = false,
}) => {
  const brandName = contentData?.data?.brand || contentData?.name || 'Product';
  const variantImages = contentData?.data?.variant_images || [];

  const textElements = (data.differentiating_elements || []).filter(el => el.type === 'text' && (el.most_count > 0 || el.least_count > 0));
  const designElements = (data.differentiating_elements || []).filter(el => el.type === 'design' && (el.most_count > 0 || el.least_count > 0));

  // Only surface the Differentiating Claims section when EVERY variant has a genuine
  // unique claim. If any variant has none, the comparison is misleading (a variant would
  // otherwise show a fabricated "no unique claims" placeholder), so hide the whole section.
  const variantIndices = variantImages.map((_, idx) => idx + 1);
  const everyVariantHasTextClaim =
    variantIndices.length > 0 &&
    variantIndices.every(vi => textElements.some(el => el.variant_index === vi));
  const totalSets = data.maxdiff_total_sets || 1;
  const boxColors = ['#07E5D1', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className={`space-y-6 ${isPrintMode ? 'print-mode overflow-visible h-auto' : ''}`}>
      {/* Winner Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
          <Award className="w-5 h-5 text-[#07E5D1] mr-2" />
          <h2 className="text-xl font-semibold text-[#1C1C1E]">
            Packaging Comparison — {brandName}
          </h2>
        </div>

        {/* Variant Images with Scores */}
        {variantImages.length > 0 && (
          <div className="mb-6">
            <div className={`grid grid-cols-${Math.min(variantImages.length, 5)} gap-4`}>
              {variantImages.map((img, idx) => {
                const isWinner = data.winner.variant_index === idx + 1;
                const score = data.ranking.find(r => r.variant_index === idx + 1)?.overall_score;
                return (
                  <div key={idx} className="flex flex-col items-center">
                    <div className="relative">
                      {isWinner && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#07E5D1] text-white text-xs font-semibold rounded z-10">WINNER</span>
                      )}
                      <img
                        src={img}
                        alt={`Variant ${idx + 1}`}
                        className={`max-h-[300px] w-auto rounded-lg border-2 ${isWinner ? 'border-[#07E5D1]' : 'border-gray-200'}`}
                      />
                    </div>
                    <span className="text-sm font-medium text-[#374151] mt-2">Variant {idx + 1}</span>
                    {score !== undefined && (
                      <div className="flex flex-col items-center mt-1">
                        <span className={`text-lg font-bold ${isWinner ? 'text-[#07E5D1]' : 'text-[#374151]'}`}>
                          {score.toFixed(1)}
                        </span>
                        <span className="text-[10px] text-[#9CA3AF]">Preference Score</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Why It Wins */}
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg border-l-4 border-l-[#07E5D1]">
          <h3 className="text-sm font-semibold text-[#1C1C1E] mb-2 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-[#07E5D1]" />
            Why {data.winner.variant_label} Wins
          </h3>
          <div className="text-sm text-[#374151]">
            <MarkdownRenderer content={data.winner.why_it_wins} />
          </div>
        </div>

        {/* Key Differences */}
        {data.key_differences && data.key_differences.length > 0 && (
          <div>
            <h3 className="text-base font-semibold text-[#1C1C1E] mb-3">Key Differences</h3>
            <div className="space-y-3">
              {data.key_differences.map((diff, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-white border border-gray-200 rounded-lg flex items-start gap-3"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <ArrowUp className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-[#1C1C1E]">{diff.element_name}</span>
                    <p className="text-xs text-[#6B7280] mt-0.5">{diff.insight}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    diff.favors_variant === data.winner.variant_index
                      ? 'text-[#07E5D1] bg-[#E8FCFA]'
                      : 'text-[#6B7280] bg-gray-100'
                  }`}>
                    Favors V{diff.favors_variant}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Differentiating Claims (text type) */}
      {everyVariantHasTextClaim && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-[#1C1C1E] mb-1">Differentiating Claims</h3>
          <p className="text-xs text-[#6B7280] mb-4">Textual claims unique to each variant</p>
          <div className={`grid grid-cols-1 md:grid-cols-${Math.min(variantImages.length, 3)} gap-6`}>
            {variantImages.map((img, idx) => {
              const variantIndex = idx + 1;
              const variantElements = textElements
                .filter(el => el.variant_index === variantIndex)
                .sort((a, b) => b.preference_score - a.preference_score);
              const elementsWithBoxes = variantElements.filter(el => el.bounding_box && (el.most_count > 0 || el.least_count > 0));

              return (
                <div key={idx}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-[#1C1C1E]">Variant {variantIndex}</span>
                    {variantIndex === data.winner.variant_index && (
                      <span className="px-1.5 py-0.5 bg-[#E8FCFA] text-[#07E5D1] text-xs font-medium rounded">Winner</span>
                    )}
                  </div>
                  {img && elementsWithBoxes.length > 0 && (
                    <div className="relative inline-block w-full mb-3 print-keep-overflow">
                      <img src={img} alt={`Variant ${variantIndex}`} className="w-full h-auto rounded-lg" />
                      {elementsWithBoxes.map((el, bIdx) => {
                        const color = boxColors[bIdx % boxColors.length];
                        const bb = el.bounding_box!;
                        return (
                          <div
                            key={bIdx}
                            className="absolute rounded"
                            style={{
                              left: `${bb.x_percent}%`,
                              top: `${bb.y_percent}%`,
                              width: `${bb.width_percent}%`,
                              bottom: `${100 - bb.y_percent - bb.height_percent}%`,
                              border: `2px solid ${color}`,
                            }}
                          >
                            <span
                              className="absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                              style={{ backgroundColor: color }}
                            >
                              {bIdx + 1}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-2 space-y-2">
                    {variantElements.map((el, elIdx) => {
                      const bboxIdx = elementsWithBoxes.indexOf(el);
                      const color = bboxIdx >= 0 ? boxColors[bboxIdx % boxColors.length] : undefined;
                      if (el.most_count === 0 && el.least_count === 0) return null;
                      const netPct = Math.round((Math.abs(el.preference_score) / totalSets) * 100);
                      const isPositive = el.preference_score >= 0;
                      return (
                        <div key={elIdx}>
                          <div className="flex items-center gap-2">
                            {color ? (
                              <span
                                className="flex-shrink-0 w-4 h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center"
                                style={{ backgroundColor: color }}
                              >
                                {bboxIdx + 1}
                              </span>
                            ) : (
                              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                <span className="w-2 h-2 rounded-full bg-[#07E5D1]" />
                              </span>
                            )}
                            <span className="text-xs font-medium text-[#374151] flex-1">{el.name}</span>
                            <div className="flex items-center gap-1 flex-shrink-0 w-[90px] justify-end">
                              <span className={`text-[9px] ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                                {isPositive ? '▲' : '▼'}
                              </span>
                              <span className="text-[10px] font-semibold text-[#6B7280] w-[28px] text-right">{netPct}%</span>
                              <span className={`text-[9px] w-[40px] ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                                {isPositive ? 'liked' : 'disliked'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Design Differences (design type) */}
      {designElements.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-[#1C1C1E] mb-1">Design Differences</h3>
          <p className="text-xs text-[#6B7280] mb-4">Visual design elements that differ between variants</p>
          <div className={`grid grid-cols-1 md:grid-cols-${Math.min(variantImages.length, 3)} gap-6`}>
            {variantImages.map((img, idx) => {
              const variantIndex = idx + 1;
              const variantElements = designElements
                .filter(el => el.variant_index === variantIndex)
                .sort((a, b) => b.preference_score - a.preference_score);
              const elementsWithBoxes = variantElements.filter(el => el.bounding_box && (el.most_count > 0 || el.least_count > 0));

              return (
                <div key={idx}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-[#1C1C1E]">Variant {variantIndex}</span>
                    {variantIndex === data.winner.variant_index && (
                      <span className="px-1.5 py-0.5 bg-[#E8FCFA] text-[#07E5D1] text-xs font-medium rounded">Winner</span>
                    )}
                  </div>
                  {img && elementsWithBoxes.length > 0 && (
                    <div className="relative inline-block w-full mb-3 print-keep-overflow">
                      <img src={img} alt={`Variant ${variantIndex}`} className="w-full h-auto rounded-lg" />
                      {elementsWithBoxes.map((el, bIdx) => {
                        const color = boxColors[bIdx % boxColors.length];
                        const bb = el.bounding_box!;
                        return (
                          <div
                            key={bIdx}
                            className="absolute rounded"
                            style={{
                              left: `${bb.x_percent}%`,
                              top: `${bb.y_percent}%`,
                              width: `${bb.width_percent}%`,
                              bottom: `${100 - bb.y_percent - bb.height_percent}%`,
                              border: `2px solid ${color}`,
                            }}
                          >
                            <span
                              className="absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                              style={{ backgroundColor: color }}
                            >
                              {bIdx + 1}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-2 space-y-2">
                    {variantElements.map((el, elIdx) => {
                      const bboxIdx = elementsWithBoxes.indexOf(el);
                      const color = bboxIdx >= 0 ? boxColors[bboxIdx % boxColors.length] : undefined;
                      if (el.most_count === 0 && el.least_count === 0) return null;
                      const netPct = Math.round((Math.abs(el.preference_score) / totalSets) * 100);
                      const isPositive = el.preference_score >= 0;
                      return (
                        <div key={elIdx}>
                          <div className="flex items-center gap-2">
                            {color ? (
                              <span
                                className="flex-shrink-0 w-4 h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center"
                                style={{ backgroundColor: color }}
                              >
                                {bboxIdx + 1}
                              </span>
                            ) : (
                              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                <span className="w-2 h-2 rounded-full bg-[#07E5D1]" />
                              </span>
                            )}
                            <span className="text-xs font-medium text-[#374151] flex-1">{el.name}</span>
                            <div className="flex items-center gap-1 flex-shrink-0 w-[90px] justify-end">
                              <span className={`text-[9px] ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                                {isPositive ? '▲' : '▼'}
                              </span>
                              <span className="text-[10px] font-semibold text-[#6B7280] w-[28px] text-right">{netPct}%</span>
                              <span className={`text-[9px] w-[40px] ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                                {isPositive ? 'liked' : 'disliked'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PackagingVariantComparisonResults;